/**
 * Blog Post Author Card
 *
 * Full author profile with bio and social links
 */
'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Author } from '@/types/post';

interface BlogPostAuthorProps {
  author: Author;
}

/**
 * Extract author title from bio
 * Looks for title before "at/of Andoria AI" or before first comma
 */
function extractTitleFromBio(bio: string): string {
  if (!bio) return 'Contributor';

  // List of common professional titles/keywords
  const titleKeywords = [
    'Engineer',
    'Developer',
    'Scientist',
    'Researcher',
    'Designer',
    'Manager',
    'Director',
    'Lead',
    'Architect',
    'Founder',
    'CEO',
    'CTO',
    'CFO',
    'COO',
    'President',
    'Professor',
    'Lecturer',
    'Consultant',
    'Specialist',
    'Strategist',
    'Writer',
    'Editor',
    'Producer',
    'Analyst',
    'Advisor',
    'Investor',
    'Technologist',
    'Entrepreneur',
    'Product Owner',
    'Product Manager',
    'Data Scientist',
    'Machine Learning',
    'AI',
    'Software',
    'Full Stack',
    'Backend',
    'Frontend',
    'Cloud',
    'DevOps',
    'Security',
    'Marketing',
    'Sales',
    'Business',
    'Operations',
    'Intern',
    'Student',
    'Fellow',
    'Postdoc',
    'Partner',
    'Principal',
    'Owner',
    'Consulting',
    'Trainer',
    'Coach',
    'Mentor',
  ];

  // Try to find a title keyword in the bio
  for (const keyword of titleKeywords) {
    // Regex: match keyword as a word, optionally followed by more words, before 'at', 'of', 'for', 'with', or end of sentence
    // Dash must be last in the character class, ampersand does not need escaping
    const regex = new RegExp(
      `([A-Z][a-zA-Z\s&-]+${keyword}[a-zA-Z\s&-]*)\s+(at|of|for|with)\b|([A-Z][a-zA-Z\s&-]*${keyword}[a-zA-Z\s&-]*)[\.,]`,
      'i'
    );
    const match = bio.match(regex);
    if (match && (match[1] || match[3])) {
      return (match[1] || match[3]).trim();
    }
  }

  // Try to extract a phrase before 'at', 'of', 'for', 'with' (generic)
  const genericRegex = /([A-Z][a-zA-Z\s&-]+)\s+(at|of|for|with)\b/;
  const genericMatch = bio.match(genericRegex);
  if (genericMatch && genericMatch[1]) {
    return genericMatch[1].trim();
  }

  // Try to extract a phrase ending with a profession keyword (e.g. 'AI Engineer.')
  for (const keyword of titleKeywords) {
    const endRegex = new RegExp(`([A-Z][a-zA-Z\s&-]+${keyword})[\.,]`, 'i');
    const endMatch = bio.match(endRegex);
    if (endMatch && endMatch[1]) {
      return endMatch[1].trim();
    }
  }

  // Fallback: If bio contains a profession keyword anywhere, return the sentence containing it
  for (const keyword of titleKeywords) {
    const sentenceRegex = new RegExp(`([^.]*${keyword}[^.]*\.)`, 'i');
    const sentenceMatch = bio.match(sentenceRegex);
    if (sentenceMatch && sentenceMatch[1]) {
      // Try to extract just the title from the sentence
      const phrase = sentenceMatch[1]
        .split(' at ')[0]
        .split(' of ')[0]
        .split(' for ')[0]
        .split(' with ')[0];
      return phrase.trim();
    }
  }

  // If no clue, fallback to 'Contributor'
  return 'Contributor';
}

export default function BlogPostAuthor({ author }: BlogPostAuthorProps) {
  const authorTitle = extractTitleFromBio(author.bio || '');

  return (
    <div
      className="flex"
      style={{
        width: '1200px',
        height: '432px',
        alignItems: 'flex-start',
        gap: '32px',
        flexShrink: 0,
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <div
        className="flex flex-col justify-between items-center"
        style={{
          height: '432px',
          padding: '24px 16px',
          flexDirection: 'column',
          flex: '1 0 0',
          borderRadius: '24px',
          border: '1px solid #EDEDED',
          background: '#FAFAFA',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Avatar */}
        <div className="relative w-[88px] h-[88px]">
          <Image
            src={author.avatar}
            alt={author.name}
            width={88}
            height={88}
            className="rounded-full border-2 border-white shadow-sm object-cover"
          />
          <div className="absolute inset-0 rounded-full border border-[#EDEDED] pointer-events-none" />
        </div>

        {/* Author Info */}
        <div className="flex flex-col items-center gap-4 w-full">
          <h3 className="font-manrope font-semibold text-[24px] leading-[32px] text-black text-center">
            {author.name}
          </h3>
          <p className="font-manrope font-medium text-[16px] leading-[22px] text-[#333333] text-center">
            {authorTitle}
          </p>
        </div>
        <p className="font-manrope font-normal text-[20px] leading-[27px] text-[#666666] text-center">
          {author.bio ||
            'Lead AI Engineer at Andoria AI with 10+ years experience in machine learning and distributed systems. Passionate about building scalable AI solutions.'}
        </p>

        {/* Social Links */}
        <div className="flex flex-col items-center gap-8 w-full">
          <div className="flex flex-row items-center gap-[20px]">
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform"
              aria-label="LinkedIn Profile"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22.2857 0H1.70893C0.766071 0 0 0.776786 0 1.73036V22.2696C0 23.2232 0.766071 24 1.70893 24H22.2857C23.2286 24 24 23.2232 24 22.2696V1.73036C24 0.776786 23.2286 0 22.2857 0ZM7.25357 20.5714H3.69643V9.11786H7.25893V20.5714H7.25357ZM5.475 7.55357C4.33393 7.55357 3.4125 6.62679 3.4125 5.49107C3.4125 4.35536 4.33393 3.42857 5.475 3.42857C6.61071 3.42857 7.5375 4.35536 7.5375 5.49107C7.5375 6.63214 6.61607 7.55357 5.475 7.55357ZM20.5875 20.5714H17.0304V15C17.0304 13.6714 17.0036 11.9625 15.1821 11.9625C13.3286 11.9625 13.0446 13.4089 13.0446 14.9036V20.5714H9.4875V9.11786H12.9V10.6821H12.9482C13.425 9.78214 14.5875 8.83393 16.3179 8.83393C19.9179 8.83393 20.5875 11.2071 20.5875 14.2929V20.5714Z"
                  fill="black"
                />
              </svg>
            </a>
            <a
              href="https://x.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform"
              aria-label="X Profile"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                  fill="black"
                />
              </svg>
            </a>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform"
              aria-label="Facebook Profile"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.3882 22.954 10.125 23.8542V15.4688H7.07812V12H10.125V9.35625C10.125 6.34875 11.9166 4.6875 14.6576 4.6875C15.9701 4.6875 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.34 7.875 13.875 8.80008 13.875 9.75V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z"
                  fill="#1877F2"
                />
              </svg>
            </a>
          </div>
          <Link
            href={`/blog?author=${author.name}`}
            className="inline-flex items-center gap-1 font-manrope font-semibold text-[18px] leading-[28px] text-[#0F6EEA] hover:text-[#0989BE] transition-colors"
          >
            View all articles by {author.name}
          </Link>
        </div>
      </div>
    </div>
  );
}
